import {observable, makeObservable, action, computed, toJS} from 'mobx';
import {find, some, every, map, set, isEqual, cloneDeep, isEmpty, flatten, without, Dictionary} from 'lodash';

import Endpoint from './Endpoint';
import {generateLocalId, validateSubnetsOf} from '../utils';
import {ctrlStages, ctrlEntityTypes} from '../const';
import {speedFromString, stringFromSpeed} from '../../rackEditor/utils';
import {ISerializable, OperationType} from '../types';
import CablingMapStore from './CablingMapStore';
import Node from './Node';
import {SpeedType} from '../../rackEditor/types';

class Link implements ISerializable {
  private _backup;

  @observable id = '';
  @observable endpoint1: Endpoint | null = null;
  @observable endpoint2: Endpoint | null = null;
  @observable stage: OperationType | null = null;
  @observable label;
  @observable aggregateId = null;
  @observable type = ctrlEntityTypes.LINK;
  @observable tags;
  timestamp; // Needed to track links order in links management modal

  @observable.ref cablingMap: CablingMapStore;

  @observable peerLinkType = null;

  @action
  init(node1: Node | null, node2: Node | null) {
    this.id = generateLocalId();
    this.endpoint1 = new Endpoint({node: node1, externalId: this.id}, this.cablingMap);
    this.endpoint2 = new Endpoint({node: node2, externalId: this.id}, this.cablingMap);
    this.stage = null;
    this.tags = [];
  }

  constructor(node1: Node | null, node2: Node | null, cablingMap: CablingMapStore) {
    makeObservable(this);

    if (!cablingMap) {
      throw new Error('Link\'s constructor called without the CablingMap store');
    }
    this.timestamp = Date.now();
    this.cablingMap = cablingMap;
    this.init(node1, node2);
  }

  get isAggregate(): boolean {
    return false;
  }

  @computed
  get isAggregated(): boolean {
    return !!this.aggregateId;
  }

  @computed
  get endpoints(): [Endpoint, Endpoint] {
    return [this.endpoint1!, this.endpoint2!];
  }

  set endpoints([endpoint1, endpoint2]) {
    this.setProperty('endpoint1', endpoint1);
    this.setProperty('endpoint2', endpoint2);
  }

  @computed
  get nodesIds(): string[] {
    return map(this.endpoints, 'nodeId');
  }

  @computed
  get symmetricId(): string {
    return [this.endpoint1?.nodeId, this.endpoint2?.nodeId].sort().join('|');
  }

  orderEndpointsFor(nodeId: string): [Endpoint, Endpoint] {
    return nodeId === this.endpoint1?.nodeId ? [this.endpoint1, this.endpoint2!] : [this.endpoint2!, this.endpoint1!];
  }

  @computed
  get exists(): boolean {
    return this.stage !== ctrlStages.DELETE;
  }

  @computed
  get isUpdate(): boolean {
    return this.stage === ctrlStages.CHANGE;
  }

  @computed
  get data() {
    return toJS({
      id: this.id,
      label: this.label,
      aggregateId: this.aggregateId,
      endpoint1: this.endpoint1!.data,
      endpoint2: this.endpoint2!.data,
      tags: this.tags
    });
  }

  @computed
  get usedIps(): string[] {
    return flatten(map(this.endpoints, 'usedIps'));
  }

  get hasChanges(): boolean {
    if (this.stage === ctrlStages.ADD || this.stage === ctrlStages.DELETE) return true;
    if (!this._backup) return false;
    return !isEqual(this._backup, this.data);
  }

  cloneFor(nodeIds: Dictionary<string>): Link {
    const clone = new Link(null, null, this.cablingMap);
    clone.fillWith({
      tags: [...this.tags],
      endpoint1: this.endpoint1?.cloneFor(nodeIds),
      endpoint2: this.endpoint2?.cloneFor(nodeIds)
    });
    return clone;
  }

  @action
  commit() {
    this._backup = null;
    this.stage = null;
  }

  @action
  backup() {
    // Preserves initial state of link to be restored in case of
    // changes discard
    this._backup = cloneDeep(this.data);
    this.stage = ctrlStages.CHANGE;
  }

  @computed
  get isBackedUp(): boolean {
    return !!this._backup;
  }

  @action
  markForDeletion() {
    this.stage = ctrlStages.DELETE;
  }

  @action
  fillWith(data) {
    Object.assign(this, data);
  }

  @action
  restore(toBlank = false) {
    const result = toBlank ? new Link(null, null, this.cablingMap) : this;
    // Restores link's preserved state.
    // Updates <target> if provided in order to track changes.
    if (this.isBackedUp) {
      const {id, label, aggregateId, endpoint1, endpoint2, tags} = this._backup;
      result.fillWith({
        id,
        label,
        aggregateId,
        tags
      });
      result.endpoint1?.fillWith(endpoint1);
      result.endpoint2?.fillWith(endpoint2);
    }
    result.commit();
    return result;
  }

  @action
  setAggregateId(aggregateId = null) {
    this.aggregateId = aggregateId;
    if (aggregateId) {
      // When individual links get added to an aggregate
      // their IP address settings must be cleaned up
      this.endpoint1?.removeIps();
      this.endpoint2?.removeIps();
    }
  }

  @action
  setProperty(property: string, value: any) {
    this[property] = value;
  }

  // Checks whether the link contains IP(s) from the list of duplicates
  containsDuplicates(usedIps: Dictionary<number>): boolean {
    return some(this.usedIps, (ip) => ((usedIps?.[ip] ?? 0) > 1));
  }

  usesPort(portId: string): boolean {
    return (this.endpoint1?.portId === portId || this.endpoint2?.portId === portId);
  }

  contains(nodes: Node[], all = false): boolean {
    return this.containsNodesIds(map(nodes, 'id'), all);
  }

  containsNodesIds(nodesIds: string[], all = false): boolean {
    return (all ? every : some)(nodesIds, (id) => (id === this.endpoint1?.nodeId || id === this.endpoint2?.nodeId));
  }

  fallsWithin(nodesIds: string[]): boolean {
    return nodesIds.includes(this.endpoint1?.nodeId) && nodesIds.includes(this.endpoint2?.nodeId);
  }

  oppositeTo(nodeId: string): string | boolean {
    if (this.endpoint1?.nodeId === nodeId) {
      return this.endpoint2?.nodeId;
    } else if (this.endpoint2?.nodeId === nodeId) {
      return this.endpoint1?.nodeId;
    } else {
      return false;
    }
  }

  getEndpointFor(nodeId: string, portId: string): Endpoint | undefined {
    return find(
      [this.endpoint1, this.endpoint2] as Endpoint[],
      (endpoint) => endpoint?.correspondsTo(nodeId, portId)
    );
  }

  connectsSameNodesAs({endpoint1, endpoint2}: Link): boolean {
    return (this.endpoint1?.nodeId === endpoint1?.nodeId && this.endpoint2?.nodeId === endpoint2?.nodeId) ||
    (this.endpoint2?.nodeId === endpoint1?.nodeId && this.endpoint1?.nodeId === endpoint2?.nodeId);
  }

  @computed
  get usedTags() {
    return [
      ...this.tags,
      ...flatten(map(this.endpoints, 'tags'))
    ];
  }

  @computed
  get errors(): string[] {
    const {ipsv4SubnetErrors, ipsv6SubnetErrors} = this;
    const result: string[] = [];
    if (ipsv4SubnetErrors) {
      result.push(...ipsv4SubnetErrors);
    }
    if (ipsv6SubnetErrors) {
      result.push(...ipsv6SubnetErrors);
    }
    return result;
  }

  @computed
  get hasErrors(): boolean {
    return !isEmpty(this.errors);
  }

  @computed
  get isValid(): boolean {
    const {hasErrors, endpoint1, endpoint2} = this;
    return !hasErrors && (endpoint1?.isValid ?? false) && (endpoint2?.isValid ?? false);
  }

  @computed
  get speed(): SpeedType {
    const result = this.endpoint1?.speed ?
      this.endpoint1.speed :
      (this.endpoint2?.speed ? this.endpoint2.speed : {value: '', unit: ''});
    return result;
  }

  @computed
  get speedString(): string {
    return stringFromSpeed(this.speed);
  }

  @computed
  get ipsv4SubnetErrors(): string[] | undefined {
    return validateSubnetsOf(without(map(this.endpoints, 'ipv4Parsed'), true, false));
  }

  @computed
  get ipsv6SubnetErrors(): string[] | undefined {
    return validateSubnetsOf(without(map(this.endpoints, 'ipv6Parsed'), true, false));
  }

  @computed
  get ipsDiffer(): boolean {
    return !this.ipsv4SubnetErrors && !this.ipsv6SubnetErrors;
  }

  static deserialize(jsonValue, cablingMap: CablingMapStore): Link {
    const {aggregate_link_id: aggregateId, label, endpoints, id, speed: linkSpeed, tags} = jsonValue;
    const result = new Link(null, null, cablingMap);

    result.id = id ?? result.id;

    [result.endpoint1, result.endpoint2] = map(endpoints, (endpoint) => {
      set(endpoint, 'local.externalId', result.id);
      return Endpoint.deserialize(
        endpoint,
        cablingMap,
        speedFromString(linkSpeed)
      );
    });
    result.fillWith({label, aggregateId, tags});
    return result;
  }

  serialize() {
    return toJS({
      id: this.id,
      aggregate_link_id: this.aggregateId,
      label: this.label,
      speed: this.speedString,
      endpoints: [this.endpoint1!.serialize(), this.endpoint2!.serialize()],
      tags: this.tags
    });
  }
}

export default Link;
